import { database, storage } from './firebaseConfig';
import {
  ref as dbRef,
  set,
  onValue,
  query,
  orderByChild,
  limitToLast,
  endBefore,
  get,
  serverTimestamp,
  update
} from 'firebase/database';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { instance } from '../shared/utilites/ApiClient';
import { encryptMessage, decryptMessage } from './encryption';
const { v4: uuidv4 } = require('uuid');

// Environment - match with backend
const ENV = 'prod'; // Using staging to match backend db_name

/**
 * Create or get individual chat between two users
 */
export const createIndividualChat = async (currentUser, receiverUser) => {
  try {
    console.log('createIndividualChat called with:', { currentUser, receiverUser, ENV });

    const chatsRef = dbRef(database, `${ENV}/chats`);
    console.log('Firebase ref created:', chatsRef.toString());

    // Check if chat already exists
    // Fetch all chats and filter client-side to avoid Firebase indexing requirement
    console.log('Checking for existing chat...');
    const snapshot = await get(chatsRef);

    if (snapshot.exists()) {
      const chats = snapshot.val();

      // Find existing individual chat between these two users
      const existingChat = Object.entries(chats).find(([, chat]) => {
        if (chat.type !== 'individual' || !chat.participants) {
          return false;
        }

        // Get participant IDs from the participants object
        const participantIds = Object.values(chat.participants).map(p => p.id);

        console.log('Checking chat:', chat.id, 'Participants:', participantIds, 'Looking for:', [currentUser.employee_id, receiverUser.employee_id]);

        // Check if both users are in this chat
        return (
          participantIds.length === 2 &&
          participantIds.includes(currentUser.employee_id) &&
          participantIds.includes(receiverUser.employee_id)
        );
      });

      if (existingChat) {
        console.log('✅ Found existing chat:', existingChat[0]);
        return existingChat[0]; // Return chatId
      }

      console.log('❌ No existing chat found, creating new one');
    }

    // Create new chat with UUID to match backend format
    const chatId = uuidv4().replace(/-/g, '') + Date.now(); // UUID without hyphens + timestamp
    const newChatRef = dbRef(database, `${ENV}/chats/${chatId}`);

    const newChat = {
      id: chatId,
      participants: {
        0: {
          id: currentUser.employee_id,
          name: currentUser.name,
          email: currentUser.email || '',
          userColor: currentUser.userColor || '0xFFFFFFFF',
          status: '0',
          msgCount: 0
        },
        1: {
          id: receiverUser.employee_id,
          name: receiverUser.name,
          email: receiverUser.email || '',
          userColor: receiverUser.userColor || '0xFFFFFFFF',
          status: '0',
          msgCount: 0
        }
      },
      type: 'individual',
      name: receiverUser.name,
      createdAt: serverTimestamp(),
      createdBy: currentUser.employee_id,
      status: '0', // 0: active, 1: inactive
      visibilityType: 'Public',
      executionType: 'Manual'
    };

    await set(newChatRef, newChat);
    return chatId;

  } catch (error) {
    console.error('Error creating chat:', error);
    throw error;
  }
};

/**
 * Send a message in a chat
 */
export const sendMessage = async (chatId, senderId, senderName, content, contentType = 'text', replyTo = null) => {
  try {
    const messageId = uuidv4().replace(/-/g, '') + Date.now(); // UUID without hyphens + timestamp
    const newMessageRef = dbRef(database, `${ENV}/messages/${chatId}/${messageId}`);

    // Encrypt message content if it's text (await the promise)
    const encryptedContent = contentType === 'text' ? await encryptMessage(content) : content;

    // Get chat to fetch participants
    const chatRef = dbRef(database, `${ENV}/chats/${chatId}`);
    const chatSnapshot = await get(chatRef);

    let participantsArray = [];
    if (chatSnapshot.exists()) {
      const chatData = chatSnapshot.val();
      const participants = chatData.participants || {};

      // Convert participants object to array format that Flutter expects
      participantsArray = Object.values(participants).map(p => ({
        id: p.id,
        name: p.name,
        email: p.email || '',
        userColor: p.userColor || '0xFFFFFFFF',
        status: p.status || '0',
        msgCount: p.msgCount || 0
      }));
    }

    const message = {
      id: messageId,
      senderId,
      senderName,
      content: encryptedContent,
      contentType,
      timestamp: serverTimestamp(),
      chatId,
      status: '0', // 0: sent, 1: delivered, 2: read
      contentUrl: [],
      participants: participantsArray // Add participants array for Flutter compatibility
    };

    // Add replyTo if provided
    if (replyTo) {
      message.replyTo = {
        id: replyTo.id,
        content: replyTo.content,
        contentType: replyTo.contentType || 'text',
        senderName: replyTo.senderName
      };
    }

    await set(newMessageRef, message);

    // Update last message in chat (store encrypted)
    await update(chatRef, {
      lastMessage: encryptedContent,
      lastMessageTime: serverTimestamp(),
      lastMessageType: contentType
    });

    // Get chat participants to send notifications (reuse chatSnapshot)
    if (chatSnapshot.exists()) {
      const chatData = chatSnapshot.val();
      const participants = chatData.participants || {};

      console.log('📊 Chat data for notifications:', {
        chatId,
        hasParticipants: !!chatData.participants,
        participantsCount: Object.keys(participants).length
      });

      // Only process if participants exist
      if (Object.keys(participants).length > 0) {
        // Get all participant employee IDs except the sender
        const employeeIds = Object.values(participants)
          .map(p => p.id)
          .filter(id => id !== senderId);

        // Update message count for recipients
        const participantUpdates = {};
        Object.entries(participants).forEach(([key, participant]) => {
          if (participant.id !== senderId) {
            const currentMsgCount = parseInt(participant.msgCount) || 0;
            participantUpdates[`${ENV}/chats/${chatId}/participants/${key}/msgCount`] = currentMsgCount + 1;
          }
        });

        if (Object.keys(participantUpdates).length > 0) {
          await update(dbRef(database), participantUpdates);
        }

        console.log('📧 Sending notifications to employee IDs:', employeeIds);

        if (employeeIds.length > 0) {
          // Send notification via backend API (non-blocking, don't await)
          instance.post('/chat/sendNotification', {
            title: senderName,
            employeeIds: employeeIds,
            message: contentType === 'text' ? content : 'You have received a file attachment'
          }).catch(error => {
            console.error('Failed to send notification:', error);
            // Don't throw - notification failure shouldn't break message sending
          });
        } else {
          console.log('ℹ️ No other participants to notify');
        }
      } else {
        console.log('ℹ️ No participants object found - skipping notifications');
      }
    }

    return messageId;

  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Listen to messages in a chat (initial load with real-time updates)
 */
export const listenToMessages = (chatId, callback, limit = 50) => {
  const messagesRef = dbRef(database, `${ENV}/messages/${chatId}`);
  const messagesQuery = query(messagesRef, orderByChild('timestamp'), limitToLast(limit));

  return onValue(messagesQuery, async (snapshot) => {
    const messages = [];
    if (snapshot.exists()) {
      // Use Promise.all to decrypt all messages in parallel
      const decryptPromises = [];

      snapshot.forEach((childSnapshot) => {
        const messageData = childSnapshot.val();
        // Decrypt message content if it's text
        const decryptPromise = messageData.contentType === 'text'
          ? decryptMessage(messageData.content)
          : Promise.resolve(messageData.content);

        decryptPromises.push(
          decryptPromise.then(decryptedContent => ({
            id: childSnapshot.key,
            ...messageData,
            content: decryptedContent
          }))
        );
      });

      // Wait for all decryptions to complete
      const decryptedMessages = await Promise.all(decryptPromises);
      messages.push(...decryptedMessages);
    }
    callback(messages);
  });
};

/**
 * Load older messages for pagination (one-time fetch, not real-time)
 * @param {string} chatId - The chat ID
 * @param {number} oldestTimestamp - Timestamp of the oldest message currently loaded
 * @param {number} limit - Number of messages to load (default: 30)
 * @returns {Promise<Array>} Array of older messages
 */
export const loadOlderMessages = async (chatId, oldestTimestamp, limit = 30) => {
  try {
    const messagesRef = dbRef(database, `${ENV}/messages/${chatId}`);
    // Query messages older than the current oldest message
    const messagesQuery = query(
      messagesRef,
      orderByChild('timestamp'),
      endBefore(oldestTimestamp),
      limitToLast(limit)
    );

    const snapshot = await get(messagesQuery);
    const messages = [];

    if (snapshot.exists()) {
      const decryptPromises = [];

      snapshot.forEach((childSnapshot) => {
        const messageData = childSnapshot.val();
        // Decrypt message content if it's text
        const decryptPromise = messageData.contentType === 'text'
          ? decryptMessage(messageData.content)
          : Promise.resolve(messageData.content);

        decryptPromises.push(
          decryptPromise.then(decryptedContent => ({
            id: childSnapshot.key,
            ...messageData,
            content: decryptedContent
          }))
        );
      });

      // Wait for all decryptions to complete
      const decryptedMessages = await Promise.all(decryptPromises);
      messages.push(...decryptedMessages);
    }

    return messages;
  } catch (error) {
    console.error('Error loading older messages:', error);
    throw error;
  }
};

// Cache for decrypted messages to avoid re-decrypting
const decryptionCache = new Map();

/**
 * Get all chats for a user (optimized)
 */
export const getUserChats = (userId, callback) => {
  const chatsRef = dbRef(database, `${ENV}/chats`);

  return onValue(chatsRef, async (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const allChats = snapshot.val();

    // First pass: Filter and collect user's individual chats (synchronous, fast)
    const userChatEntries = Object.entries(allChats).filter(([, chat]) => {
      // Quick filters first (no async operations)
      if (!chat.participants || chat.type !== 'individual') {
        return false;
      }

      // Check if user is participant
      return Object.values(chat.participants).some(
        participant => participant.id === userId
      );
    });

    // Second pass: Process only user's chats with decryption
    const chatPromises = userChatEntries.map(async ([chatId, chat]) => {
      // Calculate unread count
      let unreadCount = 0;
      const userParticipant = Object.values(chat.participants).find(
        participant => participant.id === userId
      );
      if (userParticipant?.msgCount) {
        unreadCount = parseInt(userParticipant.msgCount) || 0;
      }

      // Decrypt lastMessage with caching
      let decryptedLastMessage = chat.lastMessage;
      if (chat.lastMessage && chat.lastMessageType === 'text') {
        // Create cache key from encrypted message
        const cacheKey = chat.lastMessage;

        if (decryptionCache.has(cacheKey)) {
          // Use cached decryption
          decryptedLastMessage = decryptionCache.get(cacheKey);
        } else {
          // Decrypt and cache
          try {
            decryptedLastMessage = await decryptMessage(chat.lastMessage);
            decryptionCache.set(cacheKey, decryptedLastMessage);

            // Limit cache size to prevent memory issues
            if (decryptionCache.size > 100) {
              const firstKey = decryptionCache.keys().next().value;
              decryptionCache.delete(firstKey);
            }
          } catch (error) {
            console.error('Decryption failed for chat:', chatId, error.message);
            decryptedLastMessage = chat.lastMessage;
          }
        }
      }

      return {
        id: chatId,
        ...chat,
        lastMessage: decryptedLastMessage,
        unreadCount
      };
    });

    // Wait for all decryptions in parallel
    const processedChats = await Promise.all(chatPromises);

    // Sort by last message time (most recent first)
    processedChats.sort((a, b) => {
      const timeA = a.lastMessageTime || a.createdAt || 0;
      const timeB = b.lastMessageTime || b.createdAt || 0;
      return timeB - timeA;
    });

    callback(processedChats);
  });
};

/**
 * Reset unread message count when user opens a chat
 */
export const resetUnreadCount = async (chatId, userId) => {
  try {
    const chatRef = dbRef(database, `${ENV}/chats/${chatId}`);
    const snapshot = await get(chatRef);

    if (snapshot.exists()) {
      const chat = snapshot.val();
      const participants = chat.participants || {};

      // Find the participant entry for this user
      Object.entries(participants).forEach(async ([key, participant]) => {
        if (participant.id === userId) {
          const updatePath = `${ENV}/chats/${chatId}/participants/${key}/msgCount`;
          await update(dbRef(database), { [updatePath]: 0 });
        }
      });
    }
  } catch (error) {
    console.error('Error resetting unread count:', error);
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (chatId, messageIds) => {
  try {
    const updates = {};
    messageIds.forEach(messageId => {
      updates[`${ENV}/messages/${chatId}/${messageId}/status`] = '2'; // 2: read
    });
    await update(dbRef(database), updates);
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

/**
 * Upload image to Firebase Storage and send as message
 */
export const uploadImageAndSendMessage = async (chatId, senderId, senderName, imageFile, replyTo = null) => {
  try {
    const messageId = uuidv4().replace(/-/g, '') + Date.now();
    const timestamp = Date.now();

    // Create storage path: chat_images/chatId/timestamp_filename
    const fileName = `${timestamp}_${imageFile.name}`;
    const imagePath = `chat_images/${chatId}/${fileName}`;
    const imageRef = storageRef(storage, imagePath);

    console.log('Uploading image to:', imagePath);

    // Upload image to Firebase Storage
    await uploadBytes(imageRef, imageFile);

    // Get download URL
    const downloadURL = await getDownloadURL(imageRef);
    console.log('Image uploaded successfully:', downloadURL);

    // Get chat to fetch participants
    const chatRef = dbRef(database, `${ENV}/chats/${chatId}`);
    const chatSnapshot = await get(chatRef);

    let participantsArray = [];
    if (chatSnapshot.exists()) {
      const chatData = chatSnapshot.val();
      const participants = chatData.participants || {};

      // Convert participants object to array format that Flutter expects
      participantsArray = Object.values(participants).map(p => ({
        id: p.id,
        name: p.name,
        email: p.email || '',
        userColor: p.userColor || '0xFFFFFFFF',
        status: p.status || '0',
        msgCount: p.msgCount || 0
      }));
    }

    // Create message with image
    const newMessageRef = dbRef(database, `${ENV}/messages/${chatId}/${messageId}`);

    const message = {
      id: messageId,
      senderId,
      senderName,
      content: '', // Empty content for images
      contentType: 'image',
      timestamp: serverTimestamp(),
      chatId,
      status: '0',
      contentUrl: [{
        fileUrl: downloadURL,
        fileName: imageFile.name
      }],
      participants: participantsArray // Add participants array for Flutter compatibility
    };

    // Add replyTo if provided
    if (replyTo) {
      message.replyTo = {
        id: replyTo.id,
        content: replyTo.content,
        contentType: replyTo.contentType || 'text',
        senderName: replyTo.senderName
      };
    }

    await set(newMessageRef, message);

    // Update last message in chat
    await update(chatRef, {
      lastMessage: '📷 Image',
      lastMessageTime: serverTimestamp(),
      lastMessageType: 'image'
    });

    // Get chat participants to send notifications (reuse chatSnapshot)
    if (chatSnapshot.exists()) {
      const chatData = chatSnapshot.val();
      const participants = chatData.participants || {};

      console.log('📊 Chat data for image notifications:', {
        chatId,
        hasParticipants: !!chatData.participants,
        participantsCount: Object.keys(participants).length
      });

      // Only process if participants exist
      if (Object.keys(participants).length > 0) {
        const employeeIds = Object.values(participants)
          .map(p => p.id)
          .filter(id => id !== senderId);

        // Update message count for recipients
        const participantUpdates = {};
        Object.entries(participants).forEach(([key, participant]) => {
          if (participant.id !== senderId) {
            const currentMsgCount = parseInt(participant.msgCount) || 0;
            participantUpdates[`${ENV}/chats/${chatId}/participants/${key}/msgCount`] = currentMsgCount + 1;
          }
        });

        if (Object.keys(participantUpdates).length > 0) {
          await update(dbRef(database), participantUpdates);
        }

        console.log('📧 Sending image notifications to:', employeeIds);

        if (employeeIds.length > 0) {
          // Send notification via backend API (non-blocking, don't await)
          instance.post('/chat/sendNotification', {
            title: senderName,
            employeeIds: employeeIds,
            message: 'Sent an image'
          }).catch(error => {
            console.error('Failed to send notification:', error);
          });
        } else {
          console.log('ℹ️ No other participants to notify for image');
        }
      } else {
        console.log('ℹ️ No participants object found - skipping image notifications');
      }
    }

    return messageId;

  } catch (error) {
    console.error('Error uploading image and sending message:', error);
    throw error;
  }
};
